/** The sidebar's "Workspaces" section: one collapsible page tree per membership. */
import { useTranslation } from 'react-i18next';
import { ChevronRight, Plus, Settings, Users } from 'lucide-react';
import { useStore } from '../db/context';
import { useUi } from '../state/ui';
import { PageTree } from '../components/PageTree';
import { cn } from '../lib/cn';
import { useWorkspacePages, useWorkspaces, useWorkspaceUi, type WorkspaceSummary } from './state';

export function WorkspacesSection({ activePageId }: { activePageId: string | null }) {
  const { t } = useTranslation();
  const workspaces = useWorkspaces();
  const openCreate = useWorkspaceUi((s) => s.openCreate);

  return (
    <div className="mt-3">
      <div className="flex items-center pr-2 pl-3">
        <h2 className="flex-1 pb-1 text-xs font-medium tracking-wide text-fg-muted uppercase">
          {t('workspaces.title')}
        </h2>
        <button
          type="button"
          onClick={openCreate}
          title={t('workspaces.create')}
          aria-label={t('workspaces.create')}
          className="rounded p-0.5 text-fg-muted hover:bg-bg-hover hover:text-fg"
        >
          <Plus className="size-3.5" />
        </button>
      </div>
      {workspaces.map((w) => (
        <WorkspaceRow key={w.id} workspace={w} activePageId={activePageId} />
      ))}
    </div>
  );
}

function WorkspaceRow({
  workspace,
  activePageId,
}: {
  workspace: WorkspaceSummary;
  activePageId: string | null;
}) {
  const { t } = useTranslation();
  const store = useStore();
  const { expanded, toggleExpanded, open } = useUi();
  const key = `ws:${workspace.id}`;
  const isOpen = !!expanded[key];
  const pages = useWorkspacePages(workspace.id);
  const openManage = useWorkspaceUi((s) => s.openManage);

  const addRootPage = async () => {
    const page = await store.createPage({ title: '', workspaceId: workspace.id });
    toggleExpanded(key, true);
    open({ kind: 'page', pageId: page.id });
  };

  return (
    <div>
      <div className="group mx-2 flex h-8 w-[calc(100%-1rem)] items-center gap-1 rounded-lg px-1.5 text-sm hover:bg-bg-hover">
        <button
          type="button"
          aria-label={isOpen ? t('sidebar.collapse') : t('sidebar.expand')}
          onClick={() => toggleExpanded(key)}
          className="grid size-5 shrink-0 place-items-center rounded hover:bg-bg-active"
        >
          <ChevronRight className={cn('size-3.5 transition-transform', isOpen && 'rotate-90')} />
        </button>
        <Users className="size-3.5 shrink-0 text-fg-muted" />
        <button
          type="button"
          onClick={() => toggleExpanded(key)}
          className="min-w-0 flex-1 truncate text-left"
        >
          {workspace.name}
        </button>
        <span className="hidden shrink-0 items-center gap-0.5 group-focus-within:flex group-hover:flex">
          {workspace.permission === 'write' && (
            <button
              type="button"
              title={t('workspaces.newPage', { name: workspace.name })}
              aria-label={t('workspaces.newPage', { name: workspace.name })}
              onClick={(e) => {
                e.stopPropagation();
                void addRootPage();
              }}
              className="grid size-5 place-items-center rounded text-fg-muted hover:bg-bg-active hover:text-fg"
            >
              <Plus className="size-3.5" />
            </button>
          )}
          <button
            type="button"
            title={t('workspaces.manage')}
            aria-label={t('workspaces.manage')}
            onClick={(e) => {
              e.stopPropagation();
              openManage(workspace.id);
            }}
            className="grid size-5 place-items-center rounded text-fg-muted hover:bg-bg-active hover:text-fg"
          >
            <Settings className="size-3.5" />
          </button>
        </span>
      </div>
      {isOpen && (
        <PageTree
          tree={pages?.tree ?? []}
          activePageId={activePageId}
          readOnly={workspace.permission === 'read'}
          showSeedSample={false}
          emptyMessage={t('workspaces.empty')}
        />
      )}
    </div>
  );
}
